import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
    Buildings,
    Users,
    PhoneCall,
    PauseCircle,
    SignOut,
    ShieldCheck,
    Gift,
    Trash,
    Play,
    Pause,
    X,
    CheckCircle,
    CurrencyInr,
    ChartLineUp,
    CreditCard,
    TrendUp,
    SignIn,
    Eye,
    Lifebuoy,
    DotsThreeVertical,
} from "@phosphor-icons/react";
import PlatformBillingModal from "@/components/PlatformBillingModal";
import PlatformSupportPanel from "@/components/PlatformSupportPanel";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const inr = (n) => (n == null ? "—" : `₹${Number(n).toLocaleString("en-IN")}`);

const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
    } catch {
        return "—";
    }
};

function StatCard({ icon: Icon, label, value, testid }) {
    return (
        <div className="group rounded-2xl border border-stone-200 bg-white p-6 transition-all hover:-translate-y-1 hover:border-ink/20 hover:shadow-lg" data-testid={testid}>
            <div className="flex items-center gap-2 text-stone2">
                <Icon size={18} weight="duotone" />
                <span className="text-[11px] font-bold uppercase tracking-widest">{label}</span>
            </div>
            <div className="mt-4 font-heading text-5xl font-bold tracking-tighter text-ink tabular-nums">{value}</div>
        </div>
    );
}

function StatusBadge({ org }) {
    if (org.deleted)
        return <span className="rounded-full bg-stone-200 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-stone-600">Removed</span>;
    if (org.status === "suspended")
        return <span className="rounded-full bg-red-100 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-red-700">Suspended</span>;
    return <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-emerald-700">Active</span>;
}

function AccessBadge({ org }) {
    if (org.comp_active)
        return (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-amber-700">
                <Gift size={12} weight="fill" /> Comp · {fmtDate(org.comp_until)}
            </span>
        );
    if (org.paid_active)
        return <span className="rounded-full bg-blue-100 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-blue-700">Paid</span>;
    return <span className="rounded-full bg-stone-100 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-stone-500">Unpaid</span>;
}

function CompModal({ org, onClose, onGranted }) {
    const [until, setUntil] = useState("");
    const [busy, setBusy] = useState(false);

    const grant = async (payload, label) => {
        setBusy(true);
        try {
            await api.post(`/platform/orgs/${org.id}/comp`, payload);
            toast.success(label);
            onGranted();
            onClose();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 grid place-items-center bg-ink/40 p-4" data-testid="comp-modal">
            <div className="w-full max-w-md rounded-2xl border border-stone-200 bg-white p-6 shadow-xl">
                <div className="flex items-start justify-between">
                    <div>
                        <div className="flex items-center gap-2 font-heading text-lg font-bold">
                            <Gift size={20} weight="duotone" className="text-amber-600" /> Grant complimentary access
                        </div>
                        <div className="mt-1 text-sm text-stone2">{org.name}</div>
                    </div>
                    <button onClick={onClose} data-testid="comp-modal-close" className="rounded-full p-1.5 text-stone2 hover:bg-stone-100">
                        <X size={18} />
                    </button>
                </div>

                <div className="mt-5 text-[11px] font-bold uppercase tracking-widest text-stone2">Free for</div>
                <div className="mt-2 grid grid-cols-4 gap-2">
                    {[1, 3, 6, 12].map((m) => (
                        <button
                            key={m}
                            disabled={busy}
                            data-testid={`comp-months-${m}`}
                            onClick={() => grant({ months: m }, `Granted ${m} month${m > 1 ? "s" : ""} free to ${org.name}`)}
                            className="rounded-xl border border-stone-300 py-2.5 text-sm font-semibold transition-all hover:-translate-y-0.5 hover:border-ink hover:bg-stone-50 disabled:opacity-50"
                        >
                            {m} mo
                        </button>
                    ))}
                </div>

                <div className="mt-5 text-[11px] font-bold uppercase tracking-widest text-stone2">Or until a specific date</div>
                <div className="mt-2 flex gap-2">
                    <input
                        type="date"
                        value={until}
                        data-testid="comp-until-input"
                        onChange={(e) => setUntil(e.target.value)}
                        className="w-full rounded-lg border border-stone-300 px-3 py-2.5 text-sm outline-none focus:border-ink"
                    />
                    <button
                        disabled={busy || !until}
                        data-testid="comp-until-apply"
                        onClick={() => grant({ until }, `Granted free access until ${fmtDate(until)}`)}
                        className="shrink-0 rounded-lg bg-ink px-4 text-sm font-semibold text-white disabled:opacity-40"
                    >
                        Apply
                    </button>
                </div>

                <button
                    disabled={busy}
                    data-testid="comp-revoke"
                    onClick={() => grant({}, `Revoked complimentary access for ${org.name}`)}
                    className="mt-5 w-full rounded-lg border border-red-200 py-2.5 text-sm font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50"
                >
                    Revoke complimentary access
                </button>
            </div>
        </div>
    );
}

export default function PlatformDashboard() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [orgs, setOrgs] = useState([]);
    const [stats, setStats] = useState(null);
    const [revenue, setRevenue] = useState(null);
    const [loading, setLoading] = useState(true);
    const [compOrg, setCompOrg] = useState(null);
    const [billingOrg, setBillingOrg] = useState(null);
    const [tab, setTab] = useState("workspaces");

    const load = useCallback(async () => {
        try {
            const [o, s, r] = await Promise.all([
                api.get("/platform/orgs"),
                api.get("/platform/stats"),
                api.get("/platform/revenue"),
            ]);
            setOrgs(o.data.orgs || []);
            setStats(s.data);
            setRevenue(r.data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const act = async (org, action) => {
        if (action === "delete" && !window.confirm(`Remove "${org.name}"? Members will lose access. Data is retained and can be restored.`)) return;
        try {
            if (action === "suspend") await api.post(`/platform/orgs/${org.id}/suspend`);
            else if (action === "reactivate") await api.post(`/platform/orgs/${org.id}/reactivate`);
            else if (action === "delete") await api.delete(`/platform/orgs/${org.id}`);
            toast.success(`Workspace "${org.name}" updated`);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
    };

    const impersonate = async (org, readonly = false) => {
        const verb = readonly ? "View (read-only)" : "Log in to";
        if (!window.confirm(`${verb} "${org.name}"? ${readonly ? "You'll enter as an admin but every change is disabled." : "You'll enter their workspace as an admin to troubleshoot."} A banner will let you exit back to the platform.`)) return;
        try {
            await api.post(`/platform/orgs/${org.id}/impersonate${readonly ? "?readonly=true" : ""}`);
            toast.success(`Entering ${org.name}…`);
            window.location.assign("/");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
    };

    const handleLogout = async () => {
        await logout();
        navigate("/login");
    };

    const sorted = useMemo(() => orgs, [orgs]);

    return (
        <div className="min-h-screen bg-paper text-ink" data-testid="platform-dashboard">
            {/* Top bar */}
            <header className="sticky top-0 z-50 border-b border-white/10 bg-ink text-white shadow-sm">
                <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-8">
                    <div className="flex items-center gap-3">
                        <div className="grid h-9 w-9 place-items-center rounded-xl bg-brand/20">
                            <ShieldCheck size={20} weight="fill" className="text-brand" />
                        </div>
                        <div className="leading-tight">
                            <div className="font-heading text-lg font-bold">Platform Console</div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-white/60">INPW Technologies</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="hidden text-right sm:block">
                            <div className="text-sm font-semibold" data-testid="owner-name">{user?.name}</div>
                            <div className="text-[11px] uppercase tracking-widest text-white/60">Owner</div>
                        </div>
                        <button
                            data-testid="platform-logout"
                            onClick={handleLogout}
                            className="rounded-full p-2 text-white/70 transition-colors hover:bg-white/10 hover:text-white"
                            title="Sign out"
                        >
                            <SignOut size={18} />
                        </button>
                    </div>
                </div>
            </header>

            <div className="mx-auto max-w-7xl px-6 py-10 lg:px-8">
                <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-stone2">Mission control</div>
                <h1 className="mt-2 font-heading text-4xl font-bold tracking-tight">Manage all tenants</h1>

                {/* Tabs */}
                <Tabs value={tab} onValueChange={setTab} className="mt-6">
                    <TabsList className="inline-flex h-auto gap-1 rounded-full bg-stone-100 p-1">
                        <TabsTrigger value="workspaces" data-testid="tab-workspaces"
                            className="rounded-full px-4 py-2 text-sm font-semibold data-[state=active]:bg-ink data-[state=active]:text-white data-[state=active]:shadow">
                            Workspaces
                        </TabsTrigger>
                        <TabsTrigger value="revenue" data-testid="tab-revenue"
                            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold data-[state=active]:bg-ink data-[state=active]:text-white data-[state=active]:shadow">
                            <ChartLineUp size={15} weight="duotone" /> Revenue &amp; Billing
                        </TabsTrigger>
                        <TabsTrigger value="support" data-testid="tab-support"
                            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold data-[state=active]:bg-ink data-[state=active]:text-white data-[state=active]:shadow">
                            <Lifebuoy size={15} weight="duotone" /> Support
                        </TabsTrigger>
                    </TabsList>
                </Tabs>

                {/* Support panel */}
                {tab === "support" && <PlatformSupportPanel />}

                {/* Revenue panel */}
                {tab === "revenue" && (
                    <div className="mt-6" data-testid="revenue-panel">
                        <div className="grid grid-cols-2 gap-6 lg:grid-cols-4">
                            <StatCard icon={CurrencyInr} label="MRR" value={inr(revenue?.mrr)} testid="stat-mrr" />
                            <StatCard icon={TrendUp} label="ARR" value={inr(revenue?.arr)} testid="stat-arr" />
                            <StatCard icon={CreditCard} label="Revenue (all time)" value={inr(revenue?.total_revenue)} testid="stat-total-revenue" />
                            <StatCard icon={CreditCard} label="Revenue (this month)" value={inr(revenue?.month_revenue)} testid="stat-month-revenue" />
                        </div>

                        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
                            {/* Subscription split + plan mix */}
                            <div className="rounded-2xl border border-stone-200 bg-white p-5">
                                <div className="text-[11px] font-bold uppercase tracking-widest text-stone2">Subscriptions</div>
                                <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                                    <div className="flex items-center justify-between rounded-lg bg-blue-50 px-3 py-2"><span>Paid</span><b data-testid="rev-paid">{revenue?.paid_count ?? 0}</b></div>
                                    <div className="flex items-center justify-between rounded-lg bg-emerald-50 px-3 py-2"><span>Trialing</span><b data-testid="rev-trial">{revenue?.trial_count ?? 0}</b></div>
                                    <div className="flex items-center justify-between rounded-lg bg-amber-50 px-3 py-2"><span>Complimentary</span><b data-testid="rev-comp">{revenue?.comp_count ?? 0}</b></div>
                                    <div className="flex items-center justify-between rounded-lg bg-stone-100 px-3 py-2"><span>Expired</span><b data-testid="rev-expired">{revenue?.expired_count ?? 0}</b></div>
                                </div>
                                <div className="mt-4 text-[11px] font-bold uppercase tracking-widest text-stone2">Plan mix (paid)</div>
                                <div className="mt-2 flex flex-wrap gap-2 text-xs">
                                    {Object.entries(revenue?.plan_counts || {}).length === 0 && <span className="text-stone-400">—</span>}
                                    {Object.entries(revenue?.plan_counts || {}).map(([p, c]) => (
                                        <span key={p} className="rounded-full bg-stone-100 px-3 py-1 font-semibold capitalize">{p}: {c}</span>
                                    ))}
                                </div>
                            </div>

                            {/* Trials expiring */}
                            <div className="rounded-2xl border border-stone-200 bg-white p-5">
                                <div className="text-[11px] font-bold uppercase tracking-widest text-stone2">Trials expiring (≤ 7 days)</div>
                                {(revenue?.trials_expiring || []).length === 0 ? (
                                    <div className="mt-4 text-sm text-stone2" data-testid="no-trials-expiring">No trials expiring soon.</div>
                                ) : (
                                    <div className="mt-3 space-y-2">
                                        {revenue.trials_expiring.map((t) => (
                                            <div key={t.id} className="flex items-center justify-between rounded-lg border border-stone-200 px-3 py-2 text-sm" data-testid={`trial-expiring-${t.id}`}>
                                                <span className="font-medium">{t.name}</span>
                                                <span className="text-amber-700">{t.days_left}d left · {fmtDate(t.trial_ends_at)}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Recent payments */}
                        <div className="mt-6 overflow-hidden rounded-2xl border border-stone-200 bg-white">
                            <div className="border-b border-stone-200 px-5 py-3 text-[11px] font-bold uppercase tracking-widest text-stone2">Recent payments</div>
                            {(revenue?.recent_payments || []).length === 0 ? (
                                <div className="px-5 py-8 text-center text-sm text-stone2" data-testid="no-recent-payments">No payments recorded yet.</div>
                            ) : (
                                <table className="w-full text-left text-sm">
                                    <thead className="bg-stone-50 text-[10px] uppercase tracking-widest text-stone2">
                                        <tr><th className="px-5 py-2">Workspace</th><th className="px-5 py-2">Plan</th><th className="px-5 py-2">Seats</th><th className="px-5 py-2">Amount</th><th className="px-5 py-2">Date</th></tr>
                                    </thead>
                                    <tbody className="divide-y divide-stone-100">
                                        {revenue.recent_payments.map((p, i) => (
                                            <tr key={p.payment_id || i} data-testid={`recent-payment-${i}`}>
                                                <td className="px-5 py-2 font-medium">{p.org_name}</td>
                                                <td className="px-5 py-2 capitalize">{p.plan} · {p.cycle}</td>
                                                <td className="px-5 py-2">{p.seats}</td>
                                                <td className="px-5 py-2 font-semibold">{inr(p.amount)}</td>
                                                <td className="px-5 py-2 text-stone2">{fmtDate(p.paid_at)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                )}

                {tab === "workspaces" && (<>
                {/* Stats */}
                <div className="mt-8 grid grid-cols-2 gap-6 lg:grid-cols-4">
                    <StatCard icon={Buildings} label="Workspaces" value={stats?.total_orgs ?? "—"} testid="stat-orgs" />
                    <StatCard icon={PauseCircle} label="Suspended" value={stats?.suspended_orgs ?? "—"} testid="stat-suspended" />
                    <StatCard icon={Users} label="Total users" value={stats?.total_users ?? "—"} testid="stat-users" />
                    <StatCard icon={PhoneCall} label="Total calls" value={stats?.total_calls ?? "—"} testid="stat-calls" />
                </div>

                {/* Table */}
                <div className="mt-8 overflow-hidden rounded-2xl border border-stone-200 bg-white">
                    <table className="w-full text-left text-sm">
                        <thead className="border-b border-stone-200 bg-stone-50 text-[11px] uppercase tracking-widest text-stone2">
                            <tr>
                                <th className="px-5 py-3 font-bold">Workspace</th>
                                <th className="px-5 py-3 font-bold">Status</th>
                                <th className="px-5 py-3 font-bold">Access</th>
                                <th className="px-5 py-3 font-bold">Users</th>
                                <th className="px-5 py-3 font-bold">Calls</th>
                                <th className="px-5 py-3 font-bold">Last activity</th>
                                <th className="px-5 py-3 text-right font-bold">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-100">
                            {loading && (
                                <tr><td colSpan={7} className="px-5 py-10 text-center text-stone2" data-testid="platform-loading">Loading workspaces…</td></tr>
                            )}
                            {!loading && sorted.length === 0 && (
                                <tr><td colSpan={7} className="px-5 py-10 text-center text-stone2">No workspaces yet.</td></tr>
                            )}
                            {sorted.map((org) => (
                                <tr key={org.id} data-testid={`org-row-${org.id}`} className="hover:bg-stone-50/60">
                                    <td className="px-5 py-4">
                                        <div className="font-semibold text-ink">{org.name}</div>
                                        <div className="text-xs text-stone2">{org.admin_email || "—"}</div>
                                    </td>
                                    <td className="px-5 py-4"><StatusBadge org={org} /></td>
                                    <td className="px-5 py-4"><AccessBadge org={org} /></td>
                                    <td className="px-5 py-4 tabular-nums">{org.user_count}</td>
                                    <td className="px-5 py-4 tabular-nums">{org.call_count}</td>
                                    <td className="px-5 py-4 text-stone2">{fmtDate(org.last_activity)}</td>
                                    <td className="px-5 py-4 text-right">
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <button
                                                    data-testid={`org-actions-${org.id}`}
                                                    title="Workspace actions"
                                                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-stone-200 text-stone-500 transition-colors hover:bg-stone-100 hover:text-ink"
                                                >
                                                    <DotsThreeVertical size={18} weight="bold" />
                                                </button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end" className="w-56 bg-white">
                                                <DropdownMenuLabel className="truncate">{org.name}</DropdownMenuLabel>
                                                <DropdownMenuSeparator />
                                                <DropdownMenuItem
                                                    data-testid={`impersonate-${org.id}`}
                                                    disabled={org.deleted || !org.user_count}
                                                    onClick={() => impersonate(org, false)}
                                                >
                                                    <SignIn size={15} className="mr-2 text-indigo-600" /> Log in
                                                </DropdownMenuItem>
                                                <DropdownMenuItem
                                                    data-testid={`impersonate-view-${org.id}`}
                                                    disabled={org.deleted || !org.user_count}
                                                    onClick={() => impersonate(org, true)}
                                                >
                                                    <Eye size={15} className="mr-2 text-amber-600" /> View (read-only)
                                                </DropdownMenuItem>
                                                <DropdownMenuSeparator />
                                                <DropdownMenuItem data-testid={`manage-billing-${org.id}`} onClick={() => setBillingOrg(org)}>
                                                    <CreditCard size={15} className="mr-2 text-blue-600" /> Billing &amp; pricing
                                                </DropdownMenuItem>
                                                <DropdownMenuItem data-testid={`grant-comp-${org.id}`} onClick={() => setCompOrg(org)}>
                                                    <Gift size={15} className="mr-2 text-amber-600" /> Grant free access
                                                </DropdownMenuItem>
                                                <DropdownMenuSeparator />
                                                {org.status === "suspended" ? (
                                                    <DropdownMenuItem data-testid={`reactivate-${org.id}`} onClick={() => act(org, "reactivate")}>
                                                        <Play size={15} weight="fill" className="mr-2 text-emerald-600" /> Reactivate
                                                    </DropdownMenuItem>
                                                ) : (
                                                    <DropdownMenuItem data-testid={`suspend-${org.id}`} onClick={() => act(org, "suspend")}>
                                                        <Pause size={15} weight="fill" className="mr-2 text-stone-500" /> Suspend
                                                    </DropdownMenuItem>
                                                )}
                                                {!org.deleted && (
                                                    <DropdownMenuItem
                                                        data-testid={`delete-${org.id}`}
                                                        onClick={() => act(org, "delete")}
                                                        className="text-red-600 focus:text-red-600"
                                                    >
                                                        <Trash size={15} className="mr-2" /> Remove workspace
                                                    </DropdownMenuItem>
                                                )}
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="mt-4 flex items-center gap-2 text-xs text-stone2">
                    <CheckCircle size={14} weight="fill" className="text-emerald-500" />
                    Suspended workspaces stay read-only (login allowed, calling disabled). Removed workspaces are hidden but retain data.
                </div>
                </>)}
            </div>

            {compOrg && <CompModal org={compOrg} onClose={() => setCompOrg(null)} onGranted={load} />}
            {billingOrg && <PlatformBillingModal org={billingOrg} onClose={() => setBillingOrg(null)} onSaved={load} />}
        </div>
    );
}
